import axios from "axios";
import {
  Package,
  Event,
  Game,
  InventoryItem,
  DashboardStats,
  EventSettings,
  EventFormData,
  ToyType,
  Expense,
  ExpenseCategory,
  Vehicle,
  EventVehicleUsage,
  FuelPrice,
  ToyClause,
} from "../types";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://thewolveslabs.com/divertilandia-management-system/divertilandia-backend/public/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor para logging de peticiones (debugging)
api.interceptors.request.use(
  (config) => {
    // Log de peticiones a /events para verificar calendar_mode
    if (config.url?.includes('/events')) {
      console.log('🔍 API Request to /events:', {
        url: config.url,
        fullURL: `${config.baseURL}${config.url}`,
        hasCalendarMode: config.url.includes('calendar_mode=true'),
        params: config.params
      });
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores
api.interceptors.response.use(
  (response) => {
    // Log de respuestas a /events
    if (response.config.url?.includes('/events')) {
      console.log('✅ API Response from /events:', {
        url: response.config.url,
        eventCount: Array.isArray(response.data) ? response.data.length : 'N/A',
        hasCalendarMode: response.config.url.includes('calendar_mode=true')
      });
    }
    return response;
  },
  (error) => {
    console.error("API Error:", error.response?.data || error.message);
    return Promise.reject(error);
  }
);


// Servicios para Eventos
export const eventService = {
  // Por defecto usa modo calendario optimizado (sin imágenes pesadas)
  getAll: (): Promise<Event[]> => api.get("/events?calendar_mode=true").then((res) => res.data),
  // Método optimizado para calendario (sin imágenes pesadas) - alias de getAll
  getAllForCalendar: (): Promise<Event[]> => api.get("/events?calendar_mode=true").then((res) => res.data),
  // Método para obtener eventos completos con todas las relaciones (solo usar cuando sea necesario)
  getAllComplete: (): Promise<Event[]> => api.get("/events").then((res) => res.data),
  getById: (id: string): Promise<Event> =>
    api.get(`/events/${id}`).then((res) => res.data),
  create: (eventData: EventFormData): Promise<Event> =>
    api.post("/events", eventData).then((res) => res.data),
  update: (id: string, eventData: Partial<EventFormData>): Promise<Event> =>
    api.put(`/events/${id}`, eventData).then((res) => res.data),
  delete: (id: string): Promise<void> =>
    api.delete(`/events/${id}`).then((res) => res.data),
  getByDate: (date: string): Promise<Event[]> =>
    api.get(`/events?date=${date}&calendar_mode=true`).then((res) => res.data),
  getByDateRange: (startDate: string, endDate: string): Promise<Event[]> =>
    api
      .get(`/events?start_date=${startDate}&end_date=${endDate}&calendar_mode=true`)
      .then((res) => res.data),
};

// Servicios para Juegos/Inventario
export const gameService = {
  getAll: (): Promise<Game[]> => api.get("/games").then((res) => res.data),
  getById: (id: string): Promise<Game> =>
    api.get(`/games/${id}`).then((res) => res.data),
  create: (gameData: Omit<Game, "id">): Promise<Game> =>
    api.post("/games", gameData).then((res) => res.data),
  update: (id: string, gameData: Partial<Game>): Promise<Game> =>
    api.put(`/games/${id}`, gameData).then((res) => res.data),
  delete: (id: string): Promise<void> =>
    api.delete(`/games/${id}`).then((res) => res.data),
  getByType: (type: string): Promise<Game[]> =>
    api.get(`/games?type=${type}`).then((res) => res.data),
};

export const inventoryService = {
  getAll: (): Promise<Game[]> =>
    api.get("/inventory").then((res) => res.data),
  getById: (id: string): Promise<Game> =>
    api.get(`/inventory/${id}`).then((res) => res.data),
  create: (itemData: {
    name: string;
    toy_type_id: string;
    quantity: number;
    unit_price?: number;
    condition?: string;
    image_base64?: string;
    is_active?: boolean;
    excellent_count?: number;
    good_count?: number;
    fair_count?: number;
    poor_count?: number;
    broken_count?: number;
    available_count?: number;
    in_use_count?: number;
    maintenance_count?: number;
    retired_count?: number;
  }): Promise<Game> =>
    api.post("/inventory", itemData).then((res) => res.data),
  update: (id: string, itemData: Partial<Game>): Promise<Game> =>
    api.put(`/inventory/${id}`, itemData).then((res) => res.data),
  delete: (id: string): Promise<void> =>
    api.delete(`/inventory/${id}`).then((res) => res.data),
  getAvailable: (): Promise<Game[]> =>
    api.get("/inventory/available").then((res) => res.data),
  
  // Servicio para actualizar contadores de unidades
  updateUnitCounters: (id: string, counters: {
    excellent_count?: number;
    good_count?: number;
    fair_count?: number;
    poor_count?: number;
    broken_count?: number;
    available_count?: number;
    in_use_count?: number;
    maintenance_count?: number;
    retired_count?: number;
  }): Promise<Game> =>
    api.put(`/inventory/${id}/unit-counters`, counters).then((res) => res.data),
};

// Servicios para Tipos de Juguetes
export const toyTypeService = {
  getAll: (): Promise<ToyType[]> =>
    api.get("/toy-types").then((res) => res.data),
  getById: (id: string): Promise<ToyType> =>
    api.get(`/toy-types/${id}`).then((res) => res.data),
  create: (
    toyTypeData: Omit<ToyType, "id" | "created_at" | "updated_at">
  ): Promise<ToyType> =>
    api.post("/toy-types", toyTypeData).then((res) => res.data),
  update: (
    id: string,
    toyTypeData: Partial<ToyType>
  ): Promise<ToyType> =>
    api.put(`/toy-types/${id}`, toyTypeData).then((res) => res.data),
  delete: (id: string): Promise<void> =>
    api.delete(`/toy-types/${id}`).then((res) => res.data),
  getActive: (): Promise<ToyType[]> =>
    api.get("/toy-types?active=true").then((res) => res.data),
};

export const packageService = {
  getAll: (): Promise<Package[]> =>
    api.get("/packages").then((res) => res.data),
  getById: (id: string): Promise<Package> =>
    api.get(`/packages/${id}`).then((res) => res.data),
  create: (packageData: {
    name: string;
    description?: string;
    price: number;
    image_base64?: string;
    max_age: number;
    is_active?: boolean;
    games: Array<{ game_id: string; quantity: number }>;
  }): Promise<Package> =>
    api.post("/packages", packageData).then((res) => res.data),
  update: (
    id: string,
    packageData: Partial<{
      name: string;
      description?: string;
      price: number;
      image_base64?: string;
      max_age: number;
      is_active?: boolean;
      games?: Array<{ game_id: string; quantity: number }>;
    }>
  ): Promise<Package> =>
    api.put(`/packages/${id}`, packageData).then((res) => res.data),
  delete: (id: string): Promise<void> =>
    api.delete(`/packages/${id}`).then((res) => res.data),
  getActive: (): Promise<Package[]> =>
    api.get("/packages?active=true").then((res) => res.data),
};

// Servicios para Configuraciones de Eventos
export const eventSettingsService = {
  getAll: (): Promise<EventSettings[]> =>
    api.get("/event-settings").then((res) => res.data),
  getByKey: (key: string): Promise<EventSettings> =>
    api.get(`/event-settings/${key}`).then((res) => res.data),
  update: (key: string, value: string): Promise<EventSettings> =>
    api
      .put(`/event-settings/${key}`, { setting_value: value })
      .then((res) => res.data),
};

// Servicios para Dashboard
export const dashboardService = {
  getStats: (): Promise<any> =>
    api.get("/dashboard/stats").then((res) => res.data),
  getMonthlyRevenue: (
    year: number
  ): Promise<{ month: number; revenue: number }[]> =>
    api.get(`/dashboard/revenue/${year}`).then((res) => res.data),
};

// Servicios para Gastos
export const expenseService = {
  getAll: (params?: any): Promise<Expense[]> =>
    api.get("/expenses", { params }).then((res) => res.data),
  getById: (id: string): Promise<Expense> =>
    api.get(`/expenses/${id}`).then((res) => res.data),
  create: (expenseData: Partial<Expense>): Promise<Expense> =>
    api.post("/expenses", expenseData).then((res) => res.data),
  update: (id: string, expenseData: Partial<Expense>): Promise<Expense> =>
    api.put(`/expenses/${id}`, expenseData).then((res) => res.data),
  delete: (id: string): Promise<void> =>
    api.delete(`/expenses/${id}`).then((res) => res.data),
  markAsPaid: (id: string): Promise<Expense> =>
    api.post(`/expenses/${id}/mark-paid`).then((res) => res.data),
  getSummary: (params?: any): Promise<any> =>
    api.get("/expenses-summary", { params }).then((res) => res.data),
};

// Servicios para Categorías de Gastos
export const expenseCategoryService = {
  getAll: (params?: any): Promise<ExpenseCategory[]> =>
    api.get("/expense-categories", { params }).then((res) => res.data),
  getById: (id: string): Promise<ExpenseCategory> =>
    api.get(`/expense-categories/${id}`).then((res) => res.data),
  create: (categoryData: Partial<ExpenseCategory>): Promise<ExpenseCategory> =>
    api.post("/expense-categories", categoryData).then((res) => res.data),
  update: (id: string, categoryData: Partial<ExpenseCategory>): Promise<ExpenseCategory> =>
    api.put(`/expense-categories/${id}`, categoryData).then((res) => res.data),
  delete: (id: string): Promise<void> =>
    api.delete(`/expense-categories/${id}`).then((res) => res.data),
};

// Servicios para Vehículos
export const vehicleService = {
  getAll: (params?: any): Promise<Vehicle[]> =>
    api.get("/vehicles", { params }).then((res) => res.data),
  getById: (id: string): Promise<Vehicle> =>
    api.get(`/vehicles/${id}`).then((res) => res.data),
  create: (vehicleData: Partial<Vehicle>): Promise<Vehicle> =>
    api.post("/vehicles", vehicleData).then((res) => res.data),
  update: (id: string, vehicleData: Partial<Vehicle>): Promise<Vehicle> =>
    api.put(`/vehicles/${id}`, vehicleData).then((res) => res.data),
  delete: (id: string): Promise<void> =>
    api.delete(`/vehicles/${id}`).then((res) => res.data),
  calculateFuelCost: (vehicleId: string, kilometers: number, initialBatteryPercentage?: number | null): Promise<any> =>
    api.post("/vehicles/calculate-fuel-cost", { 
      vehicle_id: vehicleId, 
      kilometers,
      initial_battery_percentage: initialBatteryPercentage 
    }).then((res) => res.data),
};

// Servicio para precios de combustible
export const fuelPriceService = {
  getAll: (): Promise<FuelPrice[]> =>
    api.get("/fuel-prices").then((res) => res.data.data || res.data),
  getById: (id: string): Promise<FuelPrice> =>
    api.get(`/fuel-prices/${id}`).then((res) => res.data.data || res.data),
  create: (fuelPriceData: Partial<FuelPrice>): Promise<FuelPrice> =>
    api.post("/fuel-prices", fuelPriceData).then((res) => res.data.data || res.data),
  update: (id: string, fuelPriceData: Partial<FuelPrice>): Promise<FuelPrice> =>
    api.put(`/fuel-prices/${id}`, fuelPriceData).then((res) => res.data.data || res.data),
  delete: (id: string): Promise<void> =>
    api.delete(`/fuel-prices/${id}`).then((res) => res.data),
  getCurrentPrices: (): Promise<FuelPrice[]> =>
    api.get("/fuel-prices/current/all").then((res) => res.data.data || res.data),
  getCurrentPrice: (fuelType: 'regular' | 'premium'): Promise<FuelPrice> =>
    api.get(`/fuel-prices/current/${fuelType}`).then((res) => res.data.data || res.data),
};

// Servicio para uso de vehículos en eventos
export const eventVehicleUsageService = {
  getAll: (): Promise<EventVehicleUsage[]> =>
    api.get("/event-vehicle-usage").then((res) => res.data.data || res.data),
  getById: (id: string): Promise<EventVehicleUsage> =>
    api.get(`/event-vehicle-usage/${id}`).then((res) => res.data.data || res.data),
  create: (usageData: Partial<EventVehicleUsage>): Promise<EventVehicleUsage> =>
    api.post("/event-vehicle-usage", usageData).then((res) => res.data.data || res.data),
  update: (id: string, usageData: Partial<EventVehicleUsage>): Promise<EventVehicleUsage> =>
    api.put(`/event-vehicle-usage/${id}`, usageData).then((res) => res.data.data || res.data),
  delete: (id: string): Promise<void> =>
    api.delete(`/event-vehicle-usage/${id}`).then((res) => res.data),
  markAsPaid: (id: string): Promise<EventVehicleUsage> =>
    api.post(`/event-vehicle-usage/${id}/mark-paid`).then((res) => res.data.data || res.data),
};

// Servicios para Cláusulas de Juguetes
export const toyClauseService = {
  getAll: (): Promise<ToyClause[]> =>
    api.get("/toy-clauses").then((res) => res.data.data || res.data),
  getById: (id: string): Promise<ToyClause> =>
    api.get(`/toy-clauses/${id}`).then((res) => res.data.data || res.data),
  create: (clauseData: { name: string; description: string; is_active?: boolean }): Promise<ToyClause> =>
    api.post("/toy-clauses", clauseData).then((res) => res.data.data || res.data),
  update: (id: string, clauseData: Partial<ToyClause>): Promise<ToyClause> =>
    api.put(`/toy-clauses/${id}`, clauseData).then((res) => res.data.data || res.data),
  delete: (id: string): Promise<void> =>
    api.delete(`/toy-clauses/${id}`).then((res) => res.data),
  getGameClauses: (gameId: string): Promise<ToyClause[]> =>
    api.get(`/games/${gameId}/clauses`).then((res) => res.data.data || res.data),
  assignClausesToGame: (gameId: string, clauseIds: string[]): Promise<void> =>
    api.post(`/games/${gameId}/clauses`, { clause_ids: clauseIds }).then((res) => res.data),
};

export default api;
